import { CookieConsent, loadConsent } from 'Utils/cookieConsent';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    [key: `ga-disable-${string}`]: boolean;
  }
}

const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;

let initialized = false;

const hasStatisticsConsent = (): boolean => {
  return loadConsent()?.statistics === true;
};

export const initializeGoogleAnalytics = (): void => {
  if (
    typeof window === 'undefined' ||
    !measurementId ||
    initialized ||
    !hasStatisticsConsent()
  ) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag = (...args: unknown[]) => {
    window.dataLayer.push(args);
  };

  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false,
  });

  const script = document.createElement('script');
  script.id = 'google-analytics-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;

  document.head.appendChild(script);

  initialized = true;
};

export const trackPageView = (path: string): void => {
  if (
    !measurementId ||
    !initialized ||
    !hasStatisticsConsent() ||
    !window.gtag
  ) {
    return;
  }

  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
};

const deleteCookie = (name: string): void => {
  document.cookie = `${name}=; Max-Age=0; path=/`;
  document.cookie = `${name}=; Max-Age=0; path=/; domain=.mr-luxury-watches.com`;
};

export const disableGoogleAnalytics = (): void => {
  if (typeof window === 'undefined') return;

  if (measurementId) {
    (window as unknown as Record<string, boolean>)[
      `ga-disable-${measurementId}`
      ] = true;
  }

  deleteCookie('_ga');
  deleteCookie('_gid');
  deleteCookie('_gat');

  Object.keys(document.cookie.split(';').reduce<Record<string, true>>((result, cookie) => {
    const name = cookie.split('=')[0]?.trim();
    if (name) result[name] = true;
    return result;
  }, {}))
    .filter((name) => name.startsWith('_ga_'))
    .forEach(deleteCookie);
};

export const handleAnalyticsConsent = (
  consent: CookieConsent | null,
): void => {
  if (consent?.statistics) {
    if (measurementId) {
      (window as unknown as Record<string, boolean>)[
        `ga-disable-${measurementId}`
        ] = false;
    }

    initializeGoogleAnalytics();

    trackPageView(
      `${window.location.pathname}${window.location.search}${window.location.hash}`,
    );
  } else {
    disableGoogleAnalytics();
  }
};
